/**
 * Leseansicht des Workload-Steckbriefs (NC-1279).
 *
 * Auto-Fakten, Gap-Warnungen und Rueckblicke kommen in P2 (#1280) und P3
 * (#1281) darunter — hier steht bewusst nur das Profil selbst.
 */

import React from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import {
  WorkloadProfile,
  ProfileAudienceScope,
  ProfileOutputTarget,
  ProfileRuntimeProfile,
} from '../../../services/types';
import { FREQUENCY_LABEL } from './profileLabels';

interface Props {
  profile: WorkloadProfile;
  canEdit: boolean;
  onEdit: () => void;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  // Trim before the truthiness check so a whitespace-only string (which the
  // backend's nonEmpty() treats as unanswered, workloadProfileValidation.ts)
  // renders the placeholder instead of a blank pre-wrap row.
  const hasValue = typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 220 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {hasValue ? value : <em>— nicht angegeben —</em>}
      </Typography>
    </Box>
  );
}

const SCOPE_LABEL: Record<ProfileAudienceScope, string> = {
  intern: 'Interne Mitarbeiter', extern: 'Externe Teilnehmer', gemischt: 'Gemischt',
};
const OUTPUT_LABEL: Record<ProfileOutputTarget, string> = {
  none: 'Nichts wird zurückgegeben',
  'collect-zip': 'Sammel-Download nach der Schulung',
  'external-scm': 'In ein externes System',
};
const RUNTIME_LABEL: Record<ProfileRuntimeProfile, string> = {
  'office-hours': 'Abends abschaltbar', '24x7': 'Muss durchlaufen',
};

export default function ProfileReadView({ profile, canEdit, onEdit }: Props) {
  const { purpose, ownership, usage, requirements, acceptance, openItems, meta } = profile;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Chip
          size="small"
          color={profile.status === 'complete' ? 'success' : 'warning'}
          label={profile.status === 'complete' ? 'Vollständig' : 'Entwurf'}
        />
        <Typography variant="caption" color="text.secondary">
          Zuletzt geändert {new Date(meta.modifiedAt).toLocaleDateString('de-DE')} von {meta.modifiedBy}
          {' · '}Wiedervorlage {new Date(meta.reviewDueUtc).toLocaleDateString('de-DE')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canEdit && <Button variant="outlined" onClick={onEdit}>Bearbeiten</Button>}
      </Stack>

      <Typography variant="subtitle1" sx={{ mt: 2 }}>Zweck &amp; Nutzen</Typography>
      <Divider />
      <Row label="Ziel" value={purpose.goal} />
      <Row label="Zielgruppe" value={purpose.audience} />
      <Row label="Teilnehmerkreis" value={SCOPE_LABEL[purpose.audienceScope]} />
      <Row label="Alternative ohne Workload" value={purpose.alternative} />

      <Typography variant="subtitle1" sx={{ mt: 3 }}>Verantwortung</Typography>
      <Divider />
      {/* Check against web-portal/api/shared/workloadProfileValidation.ts case 'ownership.contact' — name AND role AND email, not any-of. */}
      <Row label="Ansprechpartner"
        value={ownership.contact.name.trim() && ownership.contact.role.trim() && ownership.contact.email.trim()
          ? [ownership.contact.name, ownership.contact.role, ownership.contact.email].join(' · ')
          : ''} />
      <Row label="Stellvertretung"
        value={ownership.deputy ? [ownership.deputy.name, ownership.deputy.email].filter(Boolean).join(' · ') : ''} />
      <Row label="Fachbereich" value={ownership.department} />
      <Row label="Kostenstelle" value={ownership.costCenterCode} />

      <Typography variant="subtitle1" sx={{ mt: 3 }}>Nutzung</Typography>
      <Divider />
      <Row label="Häufigkeit" value={FREQUENCY_LABEL[usage.frequency]} />
      {/* Check against web-portal/api/shared/workloadProfileValidation.ts case 'usage.participantsTypical'/'usage.participantsMax' */}
      <Row label="Teilnehmer" value={usage.participantsTypical > 0 && usage.participantsMax > 0 ? `${usage.participantsTypical} typisch, max. ${usage.participantsMax}` : ''} />
      {/* Check against web-portal/api/shared/workloadProfileValidation.ts case 'usage.durationDays' */}
      <Row label="Dauer" value={usage.durationDays.min > 0 && usage.durationDays.max >= usage.durationDays.min ? `${usage.durationDays.min}–${usage.durationDays.max} Tage` : ''} />
      <Row label="Laufzeitprofil" value={RUNTIME_LABEL[usage.runtimeProfile]} />

      <Typography variant="subtitle1" sx={{ mt: 3 }}>Voraussetzungen</Typography>
      <Divider />
      <Row label="Lizenzsituation" value={requirements.licensing} />
      <Row label="Eigene Eingangsdaten" value={requirements.hasInputData ? 'Ja' : 'Nein'} />
      <Row label="Arbeitsergebnis" value={OUTPUT_LABEL[requirements.outputTarget]} />
      <Row label="Sprache" value={requirements.language === 'de-DE' ? 'Deutsch' : 'Englisch'} />
      <Row label="Besonderheiten" value={requirements.notes} />
      <Row label="Abnahme-Kriterium" value={acceptance.criterion} />

      {openItems.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mt: 3 }}>Bewusst offen</Typography>
          <Divider />
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            {openItems.map((p) => <Chip key={p} size="small" variant="outlined" label={p} />)}
          </Stack>
        </>
      )}
    </Box>
  );
}
